import { useCola } from './ColaProvider';
import { useBundleOpcional } from './BundleProvider';
import { useSinConexion } from '@/core/red/conectividad';

/**
 * Un renglón que dice en qué estado está la aplicación.
 *
 * Va en el marco, arriba de todo, porque el estado no es de una pantalla: si
 * no hay señal, no la hay en ninguna. Antes esto vivía sólo dentro de la
 * pantalla de Trabajos, así que alguien podía estar en Clientes o cargando un
 * trabajo sin ninguna indicación de que estaba trabajando sin conexión —y en
 * las capturas del teléfono, efectivamente, no aparecía por ningún lado.
 *
 * **No está siempre.** Con señal y sin nada esperando no se dibuja nada. Una
 * franja permanente deja de leerse a los dos días, y el día que importa
 * tampoco la va a leer nadie.
 *
 * Los cuatro estados y sus palabras exactas:
 *
 *   Sin conexión                            no hay nada esperando
 *   Sin conexión · Guardado en este teléfono  hay trabajos en la cola
 *   Sincronizando…                          se están enviando
 *   Sincronizado                            el servidor los confirmó
 *
 * El último sólo se dice cuando el servidor confirmó. Es la regla de toda la
 * cola y no se negocia: es preferible alguien que sabe que le faltan tres
 * trabajos por subir, a alguien que cree que están y se entera el día que
 * cierra el mes.
 *
 * Y hay un quinto estado, que se agregó después de perder dos intentos de QA
 * por su ausencia: **no se pudo preparar el modo sin conexión**. Cuando el
 * servidor rechaza la descarga de la copia —porque la función no está
 * aplicada, porque falta un permiso— el fallo era mudo. Recién se notaba horas
 * después, en el campo y en modo avión, con la pantalla diciendo que no hay
 * clientes descargados y sin manera de hacer nada al respecto.
 *
 * Ese aviso va CON señal, que es el único momento en que sirve de algo, y no
 * desaparece solo: mientras esa copia no baje, el teléfono no está preparado
 * para el campo y decirlo es más importante que no molestar.
 */
export function SenalGlobal() {
  const sinServidor = useSinConexion();
  const { altas, sincronizando } = useCola();
  const { problema } = useBundleOpcional() ?? { problema: null };

  const pendientes = altas.filter((a) => a.estado === 'pendiente' || a.estado === 'enviando');
  const conError = altas.filter((a) => a.estado === 'error');
  // Recién sincronizado: hay algo confirmado y ya no queda nada esperando.
  const reciénSubido = altas.some((a) => a.estado === 'sincronizado');

  let texto: string | null = null;
  let tono = 'bg-[var(--superficie-suave)] text-[var(--tinta-suave)]';

  if (sinServidor) {
    texto = pendientes.length > 0 ? 'Sin conexión · Guardado en este teléfono' : 'Sin conexión';
    tono = 'bg-[var(--aviso-fondo)] text-[var(--aviso)]';
  } else if (problema) {
    // Con señal y con un rechazo del servidor: es AHORA cuando se puede hacer
    // algo. El motivo va tal cual porque dice qué corregir.
    texto = `No se pudo preparar el modo sin conexión: ${problema}`;
    tono = 'bg-[var(--peligro-fondo)] text-[var(--peligro)]';
  } else if (sincronizando || pendientes.some((a) => a.estado === 'enviando')) {
    texto = 'Sincronizando…';
  } else if (conError.length > 0) {
    texto = `${conError.length} trabajo(s) no pudieron sincronizarse`;
    tono = 'bg-[var(--peligro-fondo)] text-[var(--peligro)]';
  } else if (pendientes.length > 0) {
    texto = `${pendientes.length} trabajo(s) sin sincronizar`;
    tono = 'bg-[var(--aviso-fondo)] text-[var(--aviso)]';
  } else if (reciénSubido) {
    texto = 'Sincronizado';
    tono = 'bg-[var(--exito-fondo)] text-[var(--exito)]';
  }

  if (!texto) return null;

  return (
    <p
      role="status"
      className={`px-4 py-1 text-center text-xs font-medium ${tono}`}
      data-testid="senal-global"
    >
      {texto}
    </p>
  );
}
